import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Quote } from "lucide-react";
import { Reveal } from "./Reveal";
import {
  TRUST_POINTS, SERVICES, INDUSTRIES, PROCESS, WHY_SYNANI, STATS, TESTIMONIALS,
} from "../lib/data";

export const Kicker = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-sapphire">
    <span className="w-6 h-px bg-sapphire" aria-hidden="true" />{children}
  </span>
);

export const SectionHeading = ({ kicker, title, sub, light, center }) => (
  <div className={`${center ? "text-center mx-auto" : ""} max-w-2xl`}>
    {kicker && <Kicker>{kicker}</Kicker>}
    <h2 className={`mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight ${light ? "text-white" : "text-navy"}`}>{title}</h2>
    {sub && <p className={`mt-4 text-base md:text-lg ${light ? "text-white/70" : "text-ink"}`}>{sub}</p>}
  </div>
);

export const Ribbon = () => (
  <section className="bg-navy py-5 border-y border-white/10" data-testid="marquee-ribbon">
    <Marquee speed={40} gradient={false} autoFill>
      {["IT Recruitment", "Executive Search", "Contract Staffing", "RPO Services", "Permanent Staffing", "Non-IT Recruitment", "Talent Acquisition"].map((t, i) => (
        <span key={i} className="mx-8 inline-flex items-center gap-8 font-heading font-bold text-2xl md:text-3xl text-white/90 tracking-tight">
          {t}<span className="text-sapphire text-xl">✦</span>
        </span>
      ))}
    </Marquee>
  </section>
);

export const TrustBar = () => (
  <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="trust-section">
    <Reveal><SectionHeading kicker="Why SYNANI" title="Talent Solutions Built Around Your Business" /></Reveal>
    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden">
      {TRUST_POINTS.map((p, i) => (
        <Reveal key={p.title} delay={i * 0.08}>
          <div className="bg-white p-8 h-full hover:bg-sapphire/[0.03] transition-colors group">
            <span className="font-heading font-black text-4xl text-slate-200 group-hover:text-sapphire transition-colors">0{i + 1}</span>
            <h3 className="mt-4 text-xl font-bold text-navy">{p.title}</h3>
            <p className="mt-2 text-sm text-ink leading-relaxed">{p.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const ServicesGrid = ({ showHeading = true }) => (
  <section id="services" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="services-section">
    {showHeading && <Reveal><SectionHeading kicker="Our Talent Solutions" title="One partner for every hiring need" sub="From niche technology roles to enterprise-wide staffing, we deliver the right people, faster." /></Reveal>}
    <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-5">
      {SERVICES.map((s, i) => {
        const Icon = s.icon;
        return (
          <Reveal key={s.title + i} delay={(i % 3) * 0.08} className={s.span}>
            <Link to={`/services/${s.slug}`} data-testid={`service-card-${i}`}
              className="group block h-full bg-white border border-slate-200 rounded-xl p-8 hover:-translate-y-1 hover:border-sapphire/40 transition-[transform,border-color] duration-300">
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-lg bg-navy text-white flex items-center justify-center group-hover:bg-sapphire transition-colors">
                  <Icon size={22} />
                </div>
                <ArrowUpRight className="text-slate-300 group-hover:text-sapphire transition-colors" size={22} />
              </div>
              <h3 className="mt-6 text-2xl font-bold text-navy">{s.title}</h3>
              <p className="mt-3 text-ink leading-relaxed">{s.short}</p>
            </Link>
          </Reveal>
        );
      })}
    </div>
  </section>
);

export const IndustriesGrid = () => (
  <section id="industries" className="bg-white border-y border-slate-200 py-20 md:py-28" data-testid="industries-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal><SectionHeading kicker="Coverage" title="Talent Across Industries" sub="Domain-aware recruiters delivering talent across technology and non-technology sectors." /></Reveal>
      <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {INDUSTRIES.map((ind, i) => {
          const Icon = ind.icon;
          return (
            <Reveal key={ind.name} delay={(i % 5) * 0.06}>
              <div className="group flex flex-col gap-4 p-6 rounded-xl border border-slate-200 bg-ice hover:bg-navy transition-colors duration-300 h-full" data-testid={`industry-${i}`}>
                <Icon size={26} className="text-sapphire" />
                <span className="font-semibold text-navy group-hover:text-white transition-colors leading-snug">{ind.name}</span>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const ProcessTimeline = () => (
  <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="process-section">
    <Reveal><SectionHeading kicker="How We Work" title="A clear, structured hiring process" sub="From understanding your requirement to successful hiring, SYNANI follows a structured approach designed for quality, speed and a positive candidate experience." /></Reveal>
    <div className="mt-14 grid grid-cols-1 md:grid-cols-5 gap-5">
      {PROCESS.map((step, i) => {
        const Icon = step.icon;
        return (
          <Reveal key={step.no} delay={i * 0.1}>
            <div className="relative h-full bg-white border border-slate-200 rounded-xl p-6">
              <span className="font-heading font-black text-5xl text-slate-100">{step.no}</span>
              <Icon size={24} className="text-sapphire mt-2" />
              <h3 className="mt-4 text-lg font-bold text-navy">{step.title}</h3>
              <p className="mt-2 text-sm text-ink leading-relaxed">{step.desc}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

export const WhySynani = () => (
  <section className="bg-navy relative overflow-hidden py-20 md:py-28" data-testid="why-section">
    <div className="absolute inset-0 grid-lines pointer-events-none" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal><SectionHeading light kicker="The SYNANI Edge" title="Why Choose SYNANI Talent Solutions?" /></Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
        {WHY_SYNANI.map((w, i) => (
          <Reveal key={w.title} delay={(i % 3) * 0.08}>
            <div className="border-t border-white/15 pt-6">
              <span className="font-heading font-black text-3xl text-sapphire">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 text-xl font-bold text-white">{w.title}</h3>
              <p className="mt-2 text-white/60 leading-relaxed">{w.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const StatsSection = () => (
  <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="stats-section">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08}>
          <div className="text-center md:text-left">
            <div className="font-heading font-black text-5xl md:text-6xl tracking-tighter text-navy">{s.value}</div>
            <div className="mt-2 text-sm font-medium uppercase tracking-wider text-ink">{s.label}</div>
          </div>
        </Reveal>
      ))}
    </div>
    <p className="mt-6 text-xs text-slate-400">* Figures shown are placeholders and will be updated with verified numbers.</p>
  </section>
);

export const Testimonials = () => (
  <section className="bg-white border-y border-slate-200 py-20 md:py-28" data-testid="testimonials-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal><SectionHeading kicker="Client Voices" title="Trusted by teams that are hiring" /></Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="h-full bg-ice border border-slate-200 rounded-xl p-8 flex flex-col">
              <Quote className="text-sapphire" size={28} />
              <p className="mt-4 text-navy leading-relaxed flex-1">{t.quote}</p>
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="font-bold text-navy">{t.name}</p>
                <p className="text-sm text-ink">{t.role}, {t.company}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const CtaSection = () => (
  <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="cta-section">
    <div className="relative overflow-hidden rounded-3xl bg-navy px-6 py-16 md:px-16 md:py-24">
      <div className="absolute inset-0 grid-lines pointer-events-none" />
      <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-sapphire/20 blur-3xl pointer-events-none" />
      <div className="relative max-w-2xl">
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white">Let's Build Your Next Great Team</h2>
        <p className="mt-5 text-lg text-white/70">Whether you're hiring one professional or building an entire team, SYNANI Talent Solutions is ready to help.</p>
        <Link to="/contact" data-testid="cta-talk-btn"
          className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-sapphire hover:bg-sapphire-dark text-white font-semibold transition-colors">
          Talk to Our Recruitment Team <ArrowUpRight size={18} />
        </Link>
      </div>
    </div>
  </section>
);
